/**
 * Generator Component
 * Main script generation interface for ChariAI
 */

import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Copy, Sparkles, RefreshCw, ArrowRight, ChevronLeft,
  History, Lock, LogIn, LayoutDashboard, Download, FileText, Crown, Shuffle, Gift,
  Play, Volume2, Square, Hash, Globe
} from "lucide-react";
import { AuthModal, UserMenu, GuestBanner } from "../Auth";
import { NotificationCenter } from "../NotificationCenter";
import { UpgradeModal } from "../UpgradeModal";
import { FeedbackModal, FeedbackButton } from "../FeedbackModal";
import { OnboardingModal } from "../OnboardingModal";
import { ReferralPrompt } from "../ReferralPrompt";
import { Footer } from "../Footer";
import { HistoryPanel, SegmentCard } from "./index";
import { API, xhrGet } from "../../utils/api";
import { PLATFORMS, TONES, CATEGORIES, AI_VIDEO_TOOLS, AI_IMAGE_TOOLS } from "../../constants/scriptConstants";

// Lazy-load below-the-fold ScriptShowcase to keep landing LCP fast
const ScriptShowcase = lazy(() => import("../ScriptShowcase").then(m => ({ default: m.ScriptShowcase })));

// Supported languages
const LANGUAGES = [
  { id: "english", name: "English", flag: "🇺🇸" },
  { id: "spanish", name: "Español", flag: "🇪🇸" },
  { id: "portuguese", name: "Português", flag: "🇧🇷" },
  { id: "french", name: "Français", flag: "🇫🇷" },
  { id: "german", name: "Deutsch", flag: "🇩🇪" },
];

export function Generator({ 
  user, 
  onAuthSuccess, 
  onLogout, 
  guestStatus, 
  onGuestGenerate, 
  showAuthOnMount = false, 
  userFeatures 
}) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Platform & UI state
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [platformKey, setPlatformKey] = useState(0);
  
  // Form state
  const [idea, setIdea] = useState("");
  const [tone, setTone] = useState("urgent");
  const [category, setCategory] = useState("What If");
  const [scriptLength, setScriptLength] = useState("short");
  const [language, setLanguage] = useState("english");
  const [selectedVideoTools, setSelectedVideoTools] = useState([]);
  const [selectedImageTools, setSelectedImageTools] = useState([]);
  
  // Generation state
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visibleSegments, setVisibleSegments] = useState([]);
  const [isRevealing, setIsRevealing] = useState(false);
  const [variationCount, setVariationCount] = useState(0);
  const [lastGeneratedIdea, setLastGeneratedIdea] = useState("");
  const [isBlurred, setIsBlurred] = useState(false);
  
  // TTS state
  const [ttsAudio, setTtsAudio] = useState(null);
  const [isGeneratingTts, setIsGeneratingTts] = useState(false);
  const [isPlayingTts, setIsPlayingTts] = useState(false);
  
  // Hot topic state
  const [hotTopic, setHotTopic] = useState(null);
  
  // Modal state
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(showAuthOnMount);
  const [authMode, setAuthMode] = useState("login");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState("feedback");
  const [feedbackPrefill, setFeedbackPrefill] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);

  // Check if user needs onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      if (user) {
        try {
          const { status, data } = await xhrGet(`${API}/user/profile`);
          if (status === 200 && data && !data.onboarding_completed) {
            setShowOnboarding(true);
          }
        } catch (err) {
          // Ignore errors
        }
      }
    };
    checkOnboarding();
  }, [user]);

  // Open feedback modal for bug report with error context
  const openBugReport = (errorMessage) => {
    setFeedbackType("bug_report");
    setFeedbackPrefill(`Error: ${errorMessage}\n\nWhat I was doing: `);
    setShowFeedback(true);
  };

  // Fetch hot topic on mount — deferred until browser is idle (post-LCP)
  useEffect(() => {
    const fetchHotTopic = async () => {
      try {
        const res = await xhrGet(`${API}/topics/current`);
        if (res.status === 200 && res.data?.topic) {
          setHotTopic(res.data.topic);
        }
      } catch (err) {
        // Silent fail
      }
    };
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    const handle = idle(fetchHotTopic, { timeout: 4000 });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(handle);
    };
  }, []);

  // Load script from navigation state (from dashboard)
  useEffect(() => {
    if (location.state?.loadScript) {
      const script = location.state.loadScript;
      setIdea(script.idea);
      setTone(script.tone);
      setCategory(script.category);
      setSelectedPlatform(script.platform);
      setShowGenerator(true);
      setResult({
        platform: script.platform,
        total_duration: script.platform === 'tiktok' ? '8-10 seconds' : script.platform === 'shorts' ? '9-12 seconds' : '8-12 seconds',
        tone: script.tone,
        category: script.category,
        idea: script.idea,
        segments: script.segments,
        hashtags: script.hashtags || [],
        language: script.language || "english"
      });
      setVisibleSegments(script.segments || []);
      setVariationCount(1);
      window.history.replaceState({}, document.title);
    }
    
    // Handle instant demo from showcase
    if (location.state?.instantDemo && location.state?.demoScript) {
      const demo = location.state.demoScript;
      const platform = location.state.demoPlatform || "tiktok";
      
      setIdea(location.state.demoIdea || "Demo Script");
      setSelectedPlatform(platform);
      setShowGenerator(true);
      setResult({
        platform: platform,
        total_duration: demo.total_duration,
        tone: "viral",
        category: "Viral Hook",
        idea: location.state.demoIdea,
        segments: demo.segments,
        hashtags: demo.hashtags || [],
        language: "english",
        isDemo: true
      });
      setVisibleSegments(demo.segments || []);
      setVariationCount(1);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const currentPlatform = PLATFORMS.find(p => p.id === selectedPlatform) || PLATFORMS[0];
  const otherPlatforms = PLATFORMS.filter(p => p.id !== selectedPlatform);

  // Handle instant demo from showcase - display result immediately
  const handleInstantDemo = (demoScript) => {
    const demo = demoScript.demoScript;
    const platform = demoScript.platform || "tiktok";
    
    setIdea(demoScript.idea || "Demo Script");
    setSelectedPlatform(platform);
    setShowGenerator(true);
    setResult({
      platform: platform,
      total_duration: demo.total_duration,
      tone: "viral",
      category: demoScript.niche || "Viral Hook",
      idea: demoScript.idea,
      segments: demo.segments,
      hashtags: demo.hashtags || [],
      language: "english",
      isDemo: true
    });
    setVisibleSegments(demo.segments || []);
    setVariationCount(1);
  };

  // Handle selecting a history item
  const handleSelectHistoryItem = (item, index) => {
    setSelectedHistoryIndex(index);
    setIdea(item.idea);
    setTone(item.tone);
    setCategory(item.category);
    setResult({
      platform: item.platform,
      total_duration: item.platform === 'tiktok' ? '8-10 seconds' : item.platform === 'shorts' ? '9-12 seconds' : '8-12 seconds',
      tone: item.tone,
      category: item.category,
      idea: item.idea,
      segments: item.segments
    });
    setVisibleSegments(item.segments || []);
    setVariationCount(1);
    setLastGeneratedIdea(item.idea);
    
    if (!selectedPlatform || selectedPlatform !== item.platform) {
      setSelectedPlatform(item.platform);
      setPlatformKey(prev => prev + 1);
      setShowGenerator(true);
    }
    
    setShowHistory(false);
    setSelectedHistoryIndex(null);
  };

  // Handle platform selection with animation
  const handleSelectPlatform = (platformId) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedPlatform(platformId);
      setPlatformKey(prev => prev + 1);
      setTimeout(() => {
        setShowGenerator(true);
        setIsTransitioning(false);
      }, 50);
    }, 300);
  };

  // Handle going back with animation
  const handleBack = () => {
    setIsTransitioning(true);
    setShowGenerator(false);
    setTimeout(() => {
      setSelectedPlatform(null);
      setResult(null);
      setError(null);
      setVariationCount(0);
      setLastGeneratedIdea("");
      setIsTransitioning(false);
    }, 300);
  };

  // Handle switching platforms with animation
  const handleSwitchPlatform = (platformId) => {
    if (platformId === selectedPlatform) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedPlatform(platformId);
      setPlatformKey(prev => prev + 1);
      setResult(null);
      setVisibleSegments([]);
      setVariationCount(0);
      setError(null);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 200);
  };

  // Generate script
  const handleGenerate = async (isVariation = false, autoGenerateIdea = false) => {
    if (!autoGenerateIdea && !idea.trim()) return;

    setIsLoading(true);
    setError(null);
    setResult(null);
    setVisibleSegments([]);
    setTtsAudio(null); // Reset TTS when generating new script

    const guestId = localStorage.getItem("guest_id") || `guest_${Date.now()}`;
    localStorage.setItem("guest_id", guestId);
    const sessionToken = localStorage.getItem("session_token");

    const requestBody = { 
      idea: autoGenerateIdea ? "" : idea, 
      platform: selectedPlatform, 
      tone, 
      category,
      script_length: scriptLength,
      language,
      video_tools: selectedVideoTools,
      image_tools: selectedImageTools,
      auto_generate_idea: autoGenerateIdea
    };

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/generate`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("X-Guest-ID", guestId);
    
    if (sessionToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${sessionToken}`);
    }
    xhr.withCredentials = true;

    xhr.onload = async function() {
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status === 429) {
          setError("Daily limit reached. Sign up for unlimited access!");
          setShowAuthModal(true);
          setAuthMode("signup");
          setIsLoading(false);
          return;
        }
        
        if (xhr.status !== 200) {
          throw new Error(data.detail || "Generation failed");
        }

        setResult(data);
        setIsLoading(false);
        setIsBlurred(data.is_blurred || false);
        
        if (data.auto_generated_idea) {
          setIdea(data.idea);
        }
        
        if (!user) {
          onGuestGenerate();
        }
        
        const currentIdea = data.idea || idea;
        if (isVariation && currentIdea === lastGeneratedIdea) {
          setVariationCount(prev => prev + 1);
        } else {
          setVariationCount(1);
          setLastGeneratedIdea(currentIdea);
        }
        
        if (data.segments && data.segments.length > 0) {
          setIsRevealing(true);
          for (let i = 0; i < data.segments.length; i++) {
            await new Promise(resolve => setTimeout(resolve, 400));
            setVisibleSegments(prev => [...prev, data.segments[i]]);
          }
          setIsRevealing(false);
          
          // Show referral prompt if user has 2 or fewer scripts remaining
          if (user && data.features?.remaining !== undefined && data.features.remaining <= 2) {
            // Only show once per session per day
            const today = new Date().toDateString();
            const lastPromptDate = localStorage.getItem("referral_prompt_date");
            if (lastPromptDate !== today) {
              setTimeout(() => {
                setShowReferralPrompt(true);
                localStorage.setItem("referral_prompt_date", today);
              }, 1500);
            }
          }
        }
      } catch (err) {
        setError(err.message || "Generation failed");
        setIsLoading(false);
      }
    };

    xhr.onerror = function() {
      setError("Network error. Please check your connection.");
      setIsLoading(false);
    };

    xhr.send(JSON.stringify(requestBody));
  };

  // Generate TTS audio preview
  const handleGenerateTts = async () => {
    if (!result?.segments || !user) return;
    
    setIsGeneratingTts(true);
    setTtsAudio(null);
    
    try {
      const sessionToken = localStorage.getItem("session_token");
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/tts/preview`, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      
      if (sessionToken) {
        xhr.setRequestHeader("Authorization", `Bearer ${sessionToken}`);
      }
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          setTtsAudio(data);
        } else {
          console.error("TTS generation failed");
        }
        setIsGeneratingTts(false);
      };
      
      xhr.onerror = function() {
        console.error("TTS request error");
        setIsGeneratingTts(false);
      };
      
      xhr.send(JSON.stringify({
        segments: result.segments,
        platform: selectedPlatform
      }));
    } catch (err) {
      console.error("TTS error:", err);
      setIsGeneratingTts(false);
    }
  };

  // Play/Stop TTS audio
  const handlePlayTts = () => {
    if (!ttsAudio?.audio) return;
    
    if (isPlayingTts) {
      // Stop
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(el => el.pause());
      setIsPlayingTts(false);
    } else {
      // Play
      const audio = new Audio(`data:audio/mp3;base64,${ttsAudio.audio}`);
      audio.onended = () => setIsPlayingTts(false);
      audio.play();
      setIsPlayingTts(true);
    }
  };

  // Use hot topic
  const handleUseHotTopic = () => {
    if (!hotTopic) return;
    setIdea(hotTopic.suggested_idea);
    if (hotTopic.category && CATEGORIES.includes(hotTopic.category)) {
      setCategory(hotTopic.category);
    }
  };

  // Copy all script content
  const handleCopyAll = async () => {
    if (!result?.segments) return;
    
    const platformName = selectedPlatform === "shorts" ? "YOUTUBE SHORTS" : selectedPlatform === "reels" ? "INSTAGRAM REELS" : "TIKTOK";
    let fullScript = `${platformName} SCRIPT: ${result.idea}\n`;
    fullScript += `Tone: ${result.tone} | Category: ${result.category}\n`;
    fullScript += `Duration: ${result.total_duration}\n`;
    
    // Add hashtags if available
    if (result.hashtags && result.hashtags.length > 0) {
      fullScript += `\nHASHTAGS: ${result.hashtags.join(' ')}\n`;
    }
    
    fullScript += `\n`;
    
    result.segments.forEach(seg => {
      fullScript += `━━━ ${seg.name} (${seg.time_range}) ━━━\n`;
      if (seg.caption) fullScript += `CAPTION: ${seg.caption}\n`;
      if (seg.voiceover) fullScript += `VOICEOVER: "${seg.voiceover}"\n`;
      if (seg.voice_direction) fullScript += `DELIVERY: ${seg.voice_direction}\n`;
      if (seg.visual) {
        fullScript += `VISUAL:\n`;
        if (seg.visual.camera_angle) fullScript += `  Camera: ${seg.visual.camera_angle}\n`;
        if (seg.visual.camera_movement) fullScript += `  Movement: ${seg.visual.camera_movement}\n`;
        if (seg.visual.lighting) fullScript += `  Lighting: ${seg.visual.lighting}\n`;
        if (seg.visual.subject) fullScript += `  Subject: ${seg.visual.subject}\n`;
      }
      if (seg.audio) {
        if (seg.audio.sound_effect) fullScript += `SFX: ${seg.audio.sound_effect}\n`;
        if (seg.audio.music_mood) fullScript += `MUSIC: ${seg.audio.music_mood}\n`;
      }
      if (seg.transition) fullScript += `TRANSITION: ${seg.transition}\n`;
      fullScript += `\n`;
    });
    
    await navigator.clipboard.writeText(fullScript);
  };

  // Export script as PDF or TXT
  const handleExport = async (format) => {
    if (!result?.history_id) return;
    
    const sessionToken = localStorage.getItem("session_token");
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${API}/scripts/${result.history_id}/export/${format}`, true);
    xhr.responseType = "blob";
    
    if (sessionToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${sessionToken}`);
    }
    xhr.withCredentials = true;

    xhr.onload = function() {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chariai_script_${selectedPlatform}_${result.history_id.slice(0, 8)}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError("Export failed. Please try again.");
      }
    };

    xhr.onerror = function() {
      setError("Network error during export.");
    };

    xhr.send();
  };

  // Show upgrade modal helper
  const showUpgrade = (feature) => {
    setUpgradeFeature(feature);
    setShowUpgradeModal(true);
  };

  // Show auth modal helper
  const showAuth = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  // Render header
  const renderHeader = (showBack = false) => (
    <header className="border-b border-zinc-900">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          {showBack && (
            <>
              <button 
                onClick={handleBack}
                className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="w-px h-5 sm:h-6 bg-zinc-800 hidden sm:block" />
            </>
          )}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <img src="/logo.png" alt="ChariAI" width="32" height="32" fetchpriority="high" decoding="async" className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg object-cover" />
            <span className={`font-bold text-sm sm:text-lg ${user ? 'hidden sm:inline' : ''}`}>ChariAI</span>
            <span className="text-[8px] sm:text-[10px] font-bold text-amber-500/60 bg-amber-500/10 px-1 sm:px-1.5 py-0.5 rounded uppercase tracking-wider">beta</span>
            
            {/* Script counter - in header (visible on all sizes) */}
            {((!user) || (user && user.plan !== 'unlimited')) && (
              <GuestBanner 
                remaining={guestStatus.remaining} 
                limit={guestStatus.limit}
                isGuest={!user}
                plan={user?.plan}
                onSignUp={() => { 
                  if (!user) {
                    showAuth("signup");
                  } else {
                    navigate("/pricing");
                  }
                }}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-3">
          <button
            onClick={() => navigate("/pricing")}
            className="flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:gap-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 rounded-full shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:scale-105 transition-all animate-pulse-subtle"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Plans</span>
          </button>
          {user && (
            <button
              onClick={() => navigate("/referrals")}
              className="flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:gap-2 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-full sm:rounded-lg transition-all"
              title="Invite & Earn"
            >
              <Gift className="w-4 h-4" />
              <span className="hidden md:inline">Invite</span>
            </button>
          )}
          {user && (
            <button
              onClick={() => navigate("/dashboard")}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg transition-all"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden md:inline">My Scripts</span>
            </button>
          )}
          <button
            onClick={() => setShowHistory(true)}
            data-testid={showBack ? "history-btn-generator" : "history-btn"}
            className="flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:gap-2 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-full sm:rounded-lg transition-all"
          >
            <History className="w-4 h-4" />
            <span className="hidden md:inline">History</span>
          </button>
          {user && <NotificationCenter user={user} />}
          {user ? (
            <UserMenu user={user} onLogout={onLogout} />
          ) : (
            <button
              onClick={() => showAuth("login")}
              className="flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:gap-2 sm:px-4 sm:py-1.5 text-xs sm:text-sm font-medium text-black bg-white hover:bg-zinc-100 rounded-full sm:rounded-lg transition-all"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Sign in</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );

  // Render modals
  const renderModals = () => (
    <>
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(userData) => {
          onAuthSuccess(userData);
          setShowAuthModal(false);
        }}
        initialMode={authMode}
      />
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        currentPlan={user?.plan || "free"}
      />
      <HistoryPanel 
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        onSelectItem={handleSelectHistoryItem}
        selectedItem={selectedHistoryIndex}
        user={user}
        userFeatures={userFeatures}
        onShowUpgradeModal={showUpgrade}
      />
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => {
          setShowFeedback(false);
          setFeedbackPrefill("");
          setFeedbackType("feedback");
        }}
        prefillText={feedbackPrefill}
        type={feedbackType}
      />
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
      <ReferralPrompt
        isOpen={showReferralPrompt}
        onClose={() => setShowReferralPrompt(false)}
        remaining={guestStatus?.remaining || 0}
      />
    </>
  );

  // Landing Page (Platform Selection)
  if (!selectedPlatform) {
    return (
      <div className={`min-h-screen bg-black text-white flex flex-col transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {renderModals()}
        {renderHeader(false)}

        <main className="flex-1 flex flex-col items-center px-4 py-6 sm:py-10">
          
          {/* HERO SECTION - Optimized for TikTok Ad Traffic */}
          <div className="w-full max-w-2xl text-center mb-6 animate-fade-in">

            {/* Main Headline - Ad Message Match */}
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 tracking-tight leading-tight">
              <span className="bg-gradient-to-r from-white via-white to-zinc-300 bg-clip-text text-transparent">Generate </span>
              <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-violet-500 bg-clip-text text-transparent">Viral Scripts</span>
              <br className="sm:hidden" />
              <span className="bg-gradient-to-r from-white via-white to-zinc-300 bg-clip-text text-transparent"> in Seconds</span>
            </h1>

            {/* Sub-headline with benefits */}
            <p className="text-zinc-400 text-sm sm:text-base max-w-lg mx-auto mb-4">
              AI writes your <span className="text-cyan-400 font-medium">TikTok</span>, <span className="text-pink-400 font-medium">Reels</span> & <span className="text-red-400 font-medium">Shorts</span> scripts — 
              complete with hooks, visuals & captions
            </p>

            {/* Trust Badges Row */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6 text-xs sm:text-sm">
              <div className="flex items-center gap-1.5 text-zinc-400">
                <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                <span>100% Free</span>
              </div>
              <div className="flex items-center gap-1.5 text-zinc-400">
                <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                <span>No Credit Card</span>
              </div>
              <div className="flex items-center gap-1.5 text-zinc-400">
                <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                <span>10K+ Creators</span>
              </div>
            </div>

            {/* PRIMARY CTA - Join Waitlist */}
            <div className="w-full max-w-md mx-auto mb-6">
              <button
                onClick={() => handleSelectPlatform("tiktok")}
                data-testid="join-waitlist-btn"
                className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 via-cyan-500 to-violet-500 hover:from-emerald-400 hover:via-cyan-400 hover:to-violet-400 text-black font-bold text-lg rounded-xl transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/25 flex items-center justify-center gap-2 group"
              >
                <Sparkles className="w-5 h-5" />
                <span>Try It Now — It's Free</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-center text-xs text-zinc-600 mt-2">
                ⚡ Takes 10 seconds • No spam, ever
              </p>
            </div>
          </div>

          {/* SOCIAL PROOF - Compact */}
          <div className="w-full max-w-xl mb-6 px-4">
            <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
              <div className="flex -space-x-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-orange-500 border-2 border-black"></div>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 border-2 border-black"></div>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 border-2 border-black"></div>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 border-2 border-black"></div>
              </div>
              <span>Joined by <span className="text-white font-medium">2,847 creators</span> this week</span>
            </div>
          </div>

          {/* DEMO SECTION - Show Value Instantly */}
          <div className="w-full max-w-xl animate-fade-in">
            <div className="text-center mb-3">
              <span className="text-xs text-zinc-500 uppercase tracking-wider font-medium">👇 See it in action</span>
            </div>
            <Suspense fallback={<div className="h-72 rounded-2xl border border-zinc-800 bg-zinc-900/30 animate-pulse" />}>
              <ScriptShowcase 
                onInstantDemo={handleInstantDemo}
                onGenerateOwn={(platform) => handleSelectPlatform(platform || "tiktok")}
                variant="landing"
              />
            </Suspense>
          </div>

          {/* SECONDARY CTA - Below demo */}
          <div className="w-full max-w-md mx-auto mt-6 px-4">
            <button
              onClick={() => showAuth("signup")}
              className="w-full py-3 px-6 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <span>Get Early Access Free</span>
            </button>
          </div>

          {/* BENEFITS - Quick scan */}
          <div className="w-full max-w-2xl mt-10 px-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                <div className="text-2xl mb-2">⚡</div>
                <div className="text-white font-semibold text-sm">Instant Scripts</div>
                <div className="text-zinc-500 text-xs">Full script in 10 seconds</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                <div className="text-2xl mb-2">🎯</div>
                <div className="text-white font-semibold text-sm">Viral Hooks</div>
                <div className="text-zinc-500 text-xs">Scroll-stopping openers</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                <div className="text-2xl mb-2">🎬</div>
                <div className="text-white font-semibold text-sm">Full Direction</div>
                <div className="text-zinc-500 text-xs">Visuals, audio, captions</div>
              </div>
            </div>
          </div>

        </main>

        {/* Floating Feedback Button */}
        <FeedbackButton onClick={() => {
          setFeedbackType("feedback");
          setFeedbackPrefill("");
          setShowFeedback(true);
        }} />

        <footer className="border-t border-zinc-900">
          <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6 text-center text-xs text-zinc-600">
            Create scroll-stopping hooks in seconds.
          </div>
        </footer>
      </div>
    );
  }

  // Generator Page
  return (
    <div className={`min-h-screen bg-black text-white transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
      {renderModals()}
      {renderHeader(true)}

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {((!user) || (user && user.plan !== 'unlimited')) && (
          <GuestBanner 
            remaining={guestStatus.remaining} 
            limit={guestStatus.limit}
            isGuest={!user}
            plan={user?.plan}
            onSignUp={() => { 
              if (!user) {
                showAuth("signup");
              } else {
                navigate("/pricing");
              }
            }}
          />
        )}

        {/* Platform Selector */}
        <div className="flex flex-col items-center mb-6" key={platformKey}>
          <div className="flex gap-3 mb-3 animate-fade-in">
            {otherPlatforms.map((p) => (
              <button
                key={p.id}
                onClick={() => handleSwitchPlatform(p.id)}
                disabled={isLoading}
                className="px-4 py-2 rounded-xl text-sm font-medium text-zinc-500 hover:text-zinc-300 bg-zinc-900/50 hover:bg-zinc-800/50 border border-zinc-800 transition-all duration-300 hover:scale-105"
              >
                {p.name}
                <span className="ml-2 text-xs text-zinc-600">{p.duration}</span>
              </button>
            ))}
          </div>
          
          <div className={`px-4 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl ${currentPlatform.color} shadow-lg transition-all duration-500 animate-scale-in`}>
            <div className="text-center">
              <h1 className={`text-xl sm:text-2xl md:text-3xl font-black tracking-tight ${currentPlatform.accentTextClass || 'text-white'}`}>
                {currentPlatform.name}
              </h1>
              <p className="text-xs sm:text-sm text-zinc-400 mt-0.5 sm:mt-1">{currentPlatform.style}</p>
            </div>
          </div>
        </div>

        <p className="text-zinc-400 text-xs sm:text-sm text-center mb-6 sm:mb-8 animate-fade-in px-2">
          Complete production scripts with camera, lighting, audio &amp; caption direction.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 sm:gap-6">
          {/* INPUT SECTION */}
          <div className="space-y-4 animate-slide-in-left">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 sm:p-5 space-y-4 lg:sticky lg:top-4">
              {/* Hot Topic Banner */}
              {hotTopic && (
                <div 
                  data-testid="hot-topic-banner"
                  className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-3 animate-fade-in"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-orange-400 uppercase tracking-wider flex items-center gap-1">
                          🔥 Hot Topic
                        </span>
                        {hotTopic.category && (
                          <span className="text-[10px] bg-orange-500/20 text-orange-300 px-1.5 py-0.5 rounded-full">
                            {hotTopic.category}
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-white truncate">{hotTopic.title}</h3>
                      <p className="text-xs text-zinc-400 line-clamp-2 mt-0.5">{hotTopic.description}</p>
                    </div>
                    <button
                      onClick={handleUseHotTopic}
                      disabled={isLoading}
                      data-testid="use-hot-topic-btn"
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold text-orange-400 hover:text-orange-300 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-lg transition-all disabled:opacity-50"
                    >
                      Use This
                    </button>
                  </div>
                </div>
              )}

              {/* Idea Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                    Your Video Idea
                  </label>
                  <button
                    onClick={() => handleGenerate(false, true)}
                    disabled={isLoading}
                    data-testid="surprise-me-btn"
                    className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-violet-400 hover:text-violet-300 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 rounded-lg transition-all disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Surprise Me
                  </button>
                </div>
                <textarea
                  data-testid="idea-input"
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="Enter your idea or click 'Surprise Me' to auto-generate one..."
                  className="w-full h-20 sm:h-24 bg-black border border-zinc-800 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 resize-none text-sm transition-all"
                  disabled={isLoading}
                />
                <p className="text-[10px] text-zinc-600 mt-1">
                  Leave blank and click Generate to auto-create an idea based on your selections
                </p>
              </div>

              {/* Tone Selection */}
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Tone</label>
                  {userFeatures && !userFeatures.can_select_tone && (
                    <span className="text-[10px] text-amber-400 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Basic plan
                    </span>
                  )}
                </div>
                <div className={`flex flex-wrap gap-1.5 sm:gap-2 ${userFeatures && !userFeatures.can_select_tone ? 'opacity-50 pointer-events-none' : ''}`}>
                  {TONES.map((t) => (
                    <button
                      key={t}
                      onClick={() => userFeatures?.can_select_tone !== false && setTone(t)}
                      disabled={isLoading || (userFeatures && !userFeatures.can_select_tone)}
                      className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 capitalize ${
                        tone === t
                          ? selectedPlatform === "reels" ? "bg-gradient-to-r from-[#833AB4] to-[#FD1D1D] text-white" : selectedPlatform === "shorts" ? "bg-[#FF0000] text-white" : "bg-[#25F4EE] text-black"
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {userFeatures && !userFeatures.can_select_tone && (
                  <p className="text-[10px] text-zinc-600 mt-1">Using default: {tone}</p>
                )}
              </div>

              {/* Category Selection */}
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Category</label>
                  {userFeatures && !userFeatures.can_select_category && (
                    <span className="text-[10px] text-amber-400 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Basic plan
                    </span>
                  )}
                </div>
                <div className={`flex flex-wrap gap-2 ${userFeatures && !userFeatures.can_select_category ? 'opacity-50 pointer-events-none' : ''}`}>
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      onClick={() => userFeatures?.can_select_category !== false && setCategory(c)}
                      disabled={isLoading || (userFeatures && !userFeatures.can_select_category)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                        category === c ? "bg-pink-500 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                {userFeatures && !userFeatures.can_select_category && (
                  <p className="text-[10px] text-zinc-600 mt-1">Using default: {category}</p>
                )}
              </div>

              {/* Language Selection */}
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Globe className="w-3 h-3" />
                    Language
                  </label>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => setLanguage(lang.id)}
                      disabled={isLoading}
                      className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                        language === lang.id
                          ? "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-transparent"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Script Length */}
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Script Length</label>
                  {!userFeatures?.can_generate_long_form && (
                    <span className="text-[10px] text-amber-400 flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Unlimited plan
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setScriptLength("short")}
                    disabled={isLoading}
                    className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border ${
                      scriptLength === "short"
                        ? "bg-zinc-800 text-white border-zinc-600"
                        : "bg-zinc-900/50 text-zinc-500 border-zinc-800 hover:border-zinc-700"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span>Short</span>
                      <span className="text-[10px] text-zinc-500 font-normal">8-15 seconds</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      if (userFeatures?.can_generate_long_form) {
                        setScriptLength("long");
                      } else {
                        showUpgrade("long_form");
                      }
                    }}
                    disabled={isLoading}
                    className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border relative ${
                      scriptLength === "long"
                        ? "bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/50"
                        : userFeatures?.can_generate_long_form
                        ? "bg-zinc-900/50 text-zinc-500 border-zinc-800 hover:border-amber-500/30 hover:text-amber-400"
                        : "bg-zinc-900/30 text-zinc-600 border-zinc-800/50 cursor-pointer hover:border-violet-500/30"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="flex items-center gap-1.5">
                        {!userFeatures?.can_generate_long_form && <Lock className="w-3 h-3" />}
                        Long
                        {userFeatures?.can_generate_long_form && (
                          <span className="text-[8px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-bold">PREMIUM</span>
                        )}
                      </span>
                      <span className="text-[10px] text-zinc-500 font-normal">20-60 seconds</span>
                    </div>
                  </button>
                </div>
                {userFeatures?.can_generate_long_form && guestStatus?.long_form && (
                  <p className="text-[10px] text-zinc-500 mt-2 text-center">
                    {guestStatus.long_form.remaining} of {guestStatus.long_form.limit} long-form scripts remaining today
                  </p>
                )}
              </div>

              {/* AI Tools Selection (Unlimited only) */}
              {userFeatures?.can_select_ai_tools ? (
                <div className="space-y-3 pt-2 border-t border-zinc-800">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">AI Tool Prompts</span>
                  </div>
                  
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1.5 block">Video Generation (max 2)</label>
                    <div className="flex flex-wrap gap-1.5">
                      {AI_VIDEO_TOOLS.map((tool) => (
                        <button
                          key={tool.id}
                          type="button"
                          onClick={() => {
                            if (selectedVideoTools.includes(tool.id)) {
                              setSelectedVideoTools(prev => prev.filter(t => t !== tool.id));
                            } else if (selectedVideoTools.length < 2) {
                              setSelectedVideoTools(prev => [...prev, tool.id]);
                            }
                          }}
                          disabled={isLoading || (!selectedVideoTools.includes(tool.id) && selectedVideoTools.length >= 2)}
                          className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                            selectedVideoTools.includes(tool.id)
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/50"
                              : "bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:border-zinc-600"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {tool.icon} {tool.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-[10px] text-zinc-500 mb-1.5 block">Image Generation (max 2)</label>
                    <div className="flex flex-wrap gap-1.5">
                      {AI_IMAGE_TOOLS.map((tool) => (
                        <button
                          key={tool.id}
                          type="button"
                          onClick={() => {
                            if (selectedImageTools.includes(tool.id)) {
                              setSelectedImageTools(prev => prev.filter(t => t !== tool.id));
                            } else if (selectedImageTools.length < 2) {
                              setSelectedImageTools(prev => [...prev, tool.id]);
                            }
                          }}
                          disabled={isLoading || (!selectedImageTools.includes(tool.id) && selectedImageTools.length >= 2)}
                          className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                            selectedImageTools.includes(tool.id)
                              ? "bg-violet-500/20 text-violet-400 border border-violet-500/50"
                              : "bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:border-zinc-600"
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {tool.icon} {tool.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {(selectedVideoTools.length > 0 || selectedImageTools.length > 0) && (
                    <p className="text-[10px] text-zinc-600">
                      Selected: {selectedVideoTools.length} video, {selectedImageTools.length} image tools
                    </p>
                  )}
                </div>
              ) : (
                <div className="pt-2 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5 text-zinc-600" />
                      <span className="text-[10px] text-zinc-600">AI Tool Prompts</span>
                    </div>
                    <span className="text-[10px] text-amber-500/70 flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Unlimited
                    </span>
                  </div>
                </div>
              )}

              {/* Generate Button */}
              <button
                data-testid="generate-button"
                onClick={() => handleGenerate(false, !idea.trim())}
                disabled={isLoading}
                className={`w-full py-3 ${selectedPlatform === "reels" ? "bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:opacity-90" : selectedPlatform === "shorts" ? "bg-[#FF0000] hover:bg-[#CC0000]" : "bg-[#25F4EE] hover:bg-[#00D9E0] text-black"} text-white font-bold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {!idea.trim() ? "Creating idea..." : "Generating..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {!idea.trim() ? "Generate Random Idea" : "Generate Script"}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* OUTPUT SECTION */}
          <div className="space-y-4 animate-slide-in-right">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-bold text-white">Production Script</h2>
                {result && (
                  <>
                    <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full">{result.total_duration}</span>
                    {variationCount > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedPlatform === "reels" ? "bg-[#FD1D1D]/20 text-[#FD1D1D]" : selectedPlatform === "shorts" ? "bg-[#FF0000]/20 text-[#FF0000]" : "bg-[#25F4EE]/20 text-[#25F4EE]"
                      }`}>
                        Variation #{variationCount}
                      </span>
                    )}
                  </>
                )}
                {isRevealing && (
                  <span className="text-xs text-zinc-500">{visibleSegments.length}/{result?.segments?.length || 0} segments</span>
                )}
              </div>
              {visibleSegments.length > 0 && !isLoading && (
                <div className="flex gap-2">
                  {guestStatus.features?.can_generate_variations ? (
                    <button
                      onClick={() => handleGenerate(true)}
                      disabled={isLoading || isRevealing}
                      data-testid="generate-variation-btn"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-50 ${
                        selectedPlatform === "reels" ? "bg-gradient-to-r from-[#833AB4] to-[#FD1D1D] text-white hover:opacity-90" : selectedPlatform === "shorts" ? "bg-[#FF0000] text-white hover:bg-[#CC0000]" : "bg-[#25F4EE] text-black hover:bg-[#00D9E0]"
                      } rounded-lg`}
                    >
                      <Shuffle className="w-3.5 h-3.5" />
                      New Variation
                      {variationCount > 1 && <span className="ml-1 px-1.5 py-0.5 bg-black/20 rounded text-[10px] font-bold">#{variationCount}</span>}
                    </button>
                  ) : (
                    <button
                      onClick={() => showUpgrade("variations")}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-zinc-800 text-zinc-500 hover:bg-zinc-700 hover:text-violet-400 rounded-lg transition-all cursor-pointer"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      Variations
                      <span className="text-[10px] ml-1 text-violet-400">PRO</span>
                    </button>
                  )}
                  <button
                    onClick={handleCopyAll}
                    disabled={isRevealing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-all disabled:opacity-50"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy All
                  </button>
                  {user && guestStatus.features?.can_export && result?.history_id && (
                    <>
                      <button
                        onClick={() => handleExport('pdf')}
                        disabled={isRevealing}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg transition-all disabled:opacity-50"
                      >
                        <Download className="w-3.5 h-3.5" />
                        PDF
                      </button>
                      <button
                        onClick={() => handleExport('txt')}
                        disabled={isRevealing}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg transition-all disabled:opacity-50"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        TXT
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Error State */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 animate-fade-in">
                <p className="text-red-400 text-sm mb-2">{error}</p>
                <button
                  onClick={() => openBugReport(error)}
                  className="text-xs text-zinc-500 hover:text-red-400 underline transition-colors"
                >
                  Report this issue
                </button>
              </div>
            )}

            {/* Empty State */}
            {!result && !isLoading && !error && visibleSegments.length === 0 && (
              <div className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl p-12 text-center animate-fade-in">
                <div className="w-14 h-14 mx-auto mb-4 bg-zinc-800 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-zinc-500" />
                </div>
                <p className="text-zinc-500 text-sm mb-1">No script yet</p>
                <p className="text-zinc-600 text-xs">Enter your idea and click Generate</p>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-12 text-center animate-pulse-subtle">
                <RefreshCw className="w-10 h-10 mx-auto mb-4 animate-spin" style={{ color: currentPlatform.accentColor }} />
                <p className="text-zinc-400 text-sm">Creating your production script...</p>
                <p className="text-zinc-600 text-xs mt-1">Segments will appear as they are ready</p>
              </div>
            )}

            {/* Result Segments */}
            {(visibleSegments.length > 0 || isRevealing) && !isLoading && (
              <div className="space-y-4">
                {visibleSegments.map((segment, index) => (
                  <SegmentCard 
                    key={segment.id || index} 
                    segment={segment} 
                    platform={selectedPlatform} 
                    index={index}
                    isBlurred={isBlurred && !user}
                    onUnlock={() => showAuth("signup")}
                  />
                ))}
                
                {isRevealing && visibleSegments.length < (result?.segments?.length || 0) && (
                  <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 rounded-xl animate-pulse">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: currentPlatform.accentColor, animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: currentPlatform.accentColor, animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: currentPlatform.accentColor, animationDelay: '300ms' }} />
                    </div>
                    <span className="text-zinc-500 text-sm">Generating next segment...</span>
                  </div>
                )}

                {/* Hashtags Section */}
                {result?.hashtags && result.hashtags.length > 0 && !isRevealing && (
                  <div 
                    data-testid="hashtags-section"
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 animate-fade-in"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Hash className="w-4 h-4 text-blue-400" />
                      <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Platform Hashtags</span>
                      <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                        {result.hashtags.length} tags
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.hashtags.map((tag, i) => (
                        <button
                          key={i}
                          onClick={() => navigator.clipboard.writeText(tag)}
                          className="px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs rounded-lg transition-all cursor-pointer group"
                          title="Click to copy"
                        >
                          <span className="text-blue-400">#</span>
                          {tag.replace(/^#/, '')}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(result.hashtags.join(' '))}
                      className="mt-3 flex items-center gap-1.5 text-xs text-zinc-500 hover:text-blue-400 transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      Copy all hashtags
                    </button>
                  </div>
                )}

                {/* TTS Voice Preview Section */}
                {result?.segments && result.segments.length > 0 && !isRevealing && user && (
                  <div 
                    data-testid="tts-section"
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 animate-fade-in"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-4 h-4 text-violet-400" />
                        <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Voice Preview</span>
                        {ttsAudio && (
                          <span className="text-[10px] bg-violet-500/20 text-violet-400 px-2 py-0.5 rounded-full">
                            {ttsAudio.voice_description || ttsAudio.voice}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!ttsAudio ? (
                          <button
                            onClick={handleGenerateTts}
                            disabled={isGeneratingTts}
                            data-testid="generate-tts-btn"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-400 hover:text-violet-300 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 rounded-lg transition-all disabled:opacity-50"
                          >
                            {isGeneratingTts ? (
                              <>
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3.5 h-3.5" />
                                Generate Audio
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={handlePlayTts}
                            data-testid="play-tts-btn"
                            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                              isPlayingTts
                                ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                                : "bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 border border-violet-500/30"
                            }`}
                          >
                            {isPlayingTts ? (
                              <>
                                <Square className="w-4 h-4" />
                                Stop
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4" />
                                Play Script
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-600 mt-2">
                      AI-generated voice preview optimized for {selectedPlatform === 'tiktok' ? 'TikTok' : selectedPlatform === 'reels' ? 'Instagram Reels' : 'YouTube Shorts'}
                    </p>
                  </div>
                )}

                {/* TTS for guests - prompt to sign in */}
                {result?.segments && result.segments.length > 0 && !isRevealing && !user && (
                  <div 
                    data-testid="tts-guest-prompt"
                    className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl p-4 animate-fade-in"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-4 h-4 text-zinc-600" />
                        <span className="text-xs font-medium text-zinc-500">Voice Preview</span>
                      </div>
                      <button
                        onClick={() => showAuth("signup")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-400 hover:text-violet-300 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 rounded-lg transition-all"
                      >
                        <Lock className="w-3 h-3" />
                        Sign up to unlock
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Floating Feedback Button */}
      <FeedbackButton onClick={() => {
        setFeedbackType("feedback");
        setFeedbackPrefill("");
        setShowFeedback(true);
      }} />

      <Footer />
    </div>
  );
}

export default Generator;
